/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import Image from '@components/global/Image';
import ProductItem from '@components/global/ProductItem';
import ItemDetail from '@components/global/ItemDetail';
import StatusBadge from '@components/global/StatusBadge';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Table from '@components/table/Table';
import * as Layout from '@components/screen/Layout';
import { isWeb, fullWidth } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import { convertProductMarketType, hasNoAccess } from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import { FLAG_TYPE } from '@/constants';

const TABS = [
  { label: 'Details', value: 'detail' },
  { label: 'Products', value: 'product' },
];

const limit = 10;

const NewArrivalBestSellerDetailScreen = (props) => {
  const dispatch = useDispatch();

  const [detailData, setDetailData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [products, setProducts] = useState([]);
  const [totalProduct, setTotalProduct] = useState(0);
  const [selectedTab, setSelectedTab] = useState('detail');

  const getNewArrivalBestSellerDetail = useApi(
    MsCatalog.getNewArrivalBestSellerDetail,
  );

  const showEditButton = hasNoAccess([
    'sales_supervisor',
    'branch_head',
    'finance_supervisor',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'regional_distributor_manager',
  ]);

  const tableStructure = {
    image: {
      head: 'Image',
      body: 'image',
      type: 'image',
    },
    product_name: {
      head: 'Product Name',
      body: 'product_name',
    },
    ...(showEditButton
      ? {
          action: {
            head: '',
            type: 'action_icon',
            actions: [
              {
                name: 'delete',
              },
            ],
          },
        }
      : false),
  };

  const getNewArrivalBestSellerProducts = async (id, newSkip = 0) => {
    const isMore = newSkip > 0;
    try {
      setSkip(newSkip);
      if (isMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      if (newSkip == 0) {
        const responseCount =
          await MsCatalog.getNewArrivalBestSellerProductCount(id);
        if (responseCount?.data?.data) {
          setTotalProduct(responseCount.data.data);
        } else {
          setTotalProduct(0);
        }
      }
      const response = await MsCatalog.getNewArrivalBestSellerProduct(id, {
        params: {
          skip: newSkip,
          limit: limit,
        },
      });
      if (response?.data?.data) {
        const result = response.data.data.map((item) => {
          item = convertProductMarketType(item);
          return item;
        });
        if (!isWeb && isMore) {
          const finalResult = products.concat(result);
          setProducts(finalResult);
        } else {
          setProducts(result);
        }
      } else {
        if (!isMore) {
          setProducts([]);
        }
      }
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getDetail = async (id) => {
    const response = await getNewArrivalBestSellerDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      getNewArrivalBestSellerProducts(result._id);
      setDetailData(result);
    }
  };

  const goBack = () => {
    props.navigation.replace('NewArrivalBestSellerDetailScreen', {});
    props.navigation.navigate('NewArrivalBestSellerScreen');
  };

  const onRemoveProduct = (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: async () => {
          try {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            const response = await MsCatalog.deleteNewArrivalBestSellerProduct(
              detailData.id,
              data.product.id,
              data.product_combination_id,
            );
            if (response?.data?.data?._id) {
              const newProduct = JSON.parse(JSON.stringify(products));
              const idx = newProduct.findIndex(
                (item) =>
                  item.product.id == data.product.id &&
                  item.product_combination_id == data.product_combination_id,
              );
              if (idx > -1) {
                newProduct.splice(idx, 1);
                setProducts(newProduct);
                Toast.show({
                  text1: 'Successfully deleted product',
                  type: 'success',
                });
              }
            } else {
              Toast.show({ text1: 'A system error occurred', type: 'error' });
            }
            dispatch(showPopup({ name: 'isLoading', value: false }));
          } catch (err) {
            dispatch(showPopup({ name: 'isLoading', value: false }));
            Toast.show({
              text1: err?.response?.data?.message || 'A system error occurred',
              type: 'error',
            });
          }
        },
      }),
    );
  };

  const onMapAction = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        onRemoveProduct(data);
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setSkip(0);
      setSelectedTab('detail');
      setDetailData({});
      if (props?.route?.params?.id) {
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {getNewArrivalBestSellerDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : detailData && Object.keys(detailData).length ? (
        <>
          {isWeb ? (
            <Layout.Wrapper white={false}>
              <Layout.Back
                title="Best Seller / New Arrival"
                onCallback={() => goBack()}
              />
            </Layout.Wrapper>
          ) : (
            false
          )}
          <Layout.Wrapper rounded>
            <ScrollView stickyHeaderIndices={isWeb ? [] : [0]}>
              {!isWeb ? (
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    borderBottomColor: Colors.grey0,
                    borderBottomWidth: 1,
                  }}>
                  {TABS.map((item, idx) => (
                    <TouchableOpacity
                      key={idx}
                      activeOpacity={0.8}
                      style={[
                        {
                          width: fullWidth / 2,
                          paddingVertical: 12,
                          padding: 15,
                        },
                        item.value == selectedTab
                          ? {
                              borderBottomColor: Colors.blue0,
                              borderBottomWidth: 1,
                            }
                          : false,
                      ]}
                      onPress={() => setSelectedTab(item.value)}>
                      <SociollaText
                        type={item.value == selectedTab ? 'medium' : 'regular'}
                        style={[
                          item.value == selectedTab
                            ? { color: Colors.blue0 }
                            : {},
                          { textAlign: 'center' },
                        ]}>
                        {item.label}
                      </SociollaText>
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                false
              )}
              {isWeb || selectedTab == 'detail' ? (
                <View style={styles.sectionWrapper}>
                  {isWeb ? (
                    <View
                      style={[
                        styles.rowContainer,
                        {
                          position: 'relative',
                          zIndex: 1,
                          justifyContent: 'space-between',
                        },
                      ]}>
                      <SociollaText type="medium" style={{ fontSize: 20 }}>
                        {detailData.name ? detailData.name : '-'}
                      </SociollaText>
                      <View style={{ position: 'relative' }}>
                        {showEditButton ? (
                          <Button
                            type="primary"
                            rounded
                            buttonStyle={{ width: 80, marginRight: 10 }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Edit
                              </SociollaText>
                            }
                            onPress={() =>
                              props.navigation.replace(
                                'NewArrivalBestSellerFormScreen',
                                {
                                  id: detailData._id,
                                  name: detailData.name ? detailData.name : '-',
                                  titleScreen: detailData.name
                                    ? detailData.name
                                    : '-',
                                },
                              )
                            }
                          />
                        ) : (
                          false
                        )}
                      </View>
                    </View>
                  ) : (
                    false
                  )}
                  <View
                    style={[
                      { maxWidth: isWeb ? 400 : '100%' },
                      isWeb ? { paddingVertical: 15 } : { paddingBottom: 20 },
                    ]}>
                    <ItemDetail
                      name="Name"
                      value={detailData.name ? detailData.name : '-'}
                    />
                    <ItemDetail
                      name="Description"
                      centered
                      value={
                        detailData.description ? detailData.description : '-'
                      }
                    />
                    <ItemDetail
                      name="Status"
                      centered
                      value={
                        <StatusBadge
                          status={detailData.is_active ? 'active' : 'in_active'}
                        />
                      }
                    />
                    <ItemDetail
                      name="Flag Type"
                      centered
                      value={
                        detailData.flag_type &&
                        FLAG_TYPE.find((a) => a.value == detailData.flag_type)
                          ? FLAG_TYPE.find(
                              (a) => a.value == detailData.flag_type,
                            )?.name
                          : '-'
                      }
                    />
                  </View>
                </View>
              ) : (
                false
              )}
              {isWeb ? <TitleSubSection title="PRODUCT" /> : false}
              {isWeb || selectedTab == 'product' ? (
                <Layout.FormWrapper
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: isWeb ? 20 : 15,
                    ...(!products?.length ? { justifyContent: 'center' } : {}),
                  }}>
                  {loading ? (
                    <Layout.Loader />
                  ) : !products?.length ? (
                    <View
                      style={{
                        paddingVertical: 20,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Image
                        style={{ width: 30, height: 35 }}
                        width={30}
                        height={35}
                        source={require('@assets/img/icons/no-product.svg')}
                        svg
                      />
                      <SociollaText style={{ marginVertical: 10 }}>
                        No product found
                      </SociollaText>
                    </View>
                  ) : isWeb ? (
                    <Table
                      structures={tableStructure}
                      data={products}
                      loading={loading || loadingMore}
                      withFetch={false}
                      hasNoHide
                      showFilter={false}
                      showSearch={false}
                      withoutShadow
                      onLoadMore={(val) =>
                        getNewArrivalBestSellerProducts(detailData?._id, val)
                      }
                      totalData={totalProduct}
                      skip={skip}
                      limit={limit}
                      onMapAction={(val) => onMapAction(val)}
                    />
                  ) : (
                    products.map((item, idx) => (
                      <ProductItem
                        data={item}
                        onRemove={() => onRemoveProduct(item)}
                        showRemove={isWeb}
                        isDetail
                        withoutPrice
                        key={idx}
                      />
                    ))
                  )}
                  {!isWeb ? (
                    <>
                      {loadingMore ? (
                        <View style={[styles.row, styles.buttonWrapper]}>
                          <Layout.Loader />
                        </View>
                      ) : !loadingMore &&
                        products?.length >= limit &&
                        skip <= products?.length ? (
                        <View style={[styles.row, styles.buttonWrapper]}>
                          <Button
                            rounded
                            type="primary"
                            buttonStyle={{ width: 150 }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{
                                  fontSize: 13,
                                  letterSpacing: 0.5,
                                  color: Colors.white,
                                }}>
                                Load More
                              </SociollaText>
                            }
                            onPress={() =>
                              getNewArrivalBestSellerProducts(
                                detailData?._id,
                                skip + limit,
                              )
                            }
                          />
                        </View>
                      ) : (
                        false
                      )}
                    </>
                  ) : (
                    false
                  )}
                </Layout.FormWrapper>
              ) : (
                false
              )}
            </ScrollView>
          </Layout.Wrapper>
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  buttonWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
  },
  sectionWrapper: { width: '100%', padding: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
});

export default NewArrivalBestSellerDetailScreen;
